import { Palette } from "lucide-react";
import React, { useRef, useState } from "react";
import SpringBtn from "../../common/springBtn";
import PopUp from "../../common/popUp";

function ColorPicker({ colors, onColorsChange }) {
	const colorInputRefs = useRef([]);
	const [showPresets, setShowPresets] = useState(false);

	// 预设颜色组合
	const colorPresets = [
		["#678CEC", "#D49BAE", "#BBCB50"], // 撞色糖果 (Jokolade)
		["#EDCBD2", "#80C4B7", "#E3856B"], // 温柔珊瑚 (Kindred Coffee)
		["#F9EC7E", "#E3CCB2", "#E26274"], // 郁金香田 (Tulips)
		["#3B5BA5", "#E87A5D", "#F3B941"], // 复古唱片 (Woah Nelly!)
		["#EDF4F2", "#7C8363", "#31473A"], // 森林之桥 (Forest Bridge)
		["#D5CAE4", "#E1E5EB", "#E59462"], // 薰衣草杏 (Marble)
		["#81CAD6", "#EDCD44", "#DC3E26"], // 夏日血橙 (Orange)
		["#B2456E", "#FBEAE7", "#552619"], // 树莓巧克力 (Chocolate)
		["#4AAFD5", "#91B187", "#E7A339"], // 柑橘果园 (Orange Tree)
		["#CADCFC", "#8AB6F9", "#00246B"], // 海滩假日 (Beach Towel)
		["#AE3B8B", "#1C5789", "#341514"], // 莓果深海 (Berry Deep Sea)
		["#3988A4", "#67C2D4", "#D0944D"], // 蓝鹊与沙 (Blue Magpie & Sand)
		["#67C2D4", "#D0944D", "#CB625F"], // 绿松石与赤陶 (Turquoise & Terracotta)
		["#DF3C5F", "#224193", "#6F9BD1"], // 复古电玩 (Retro Arcade)
		["#BDCBE0", "#8A904C", "#E2BAE6"], // 橄榄与丁香 (Olive & Lilac)
		["#1D71BA", "#EDC400", "#B25690"], // 活力都市 (Vibrant City)
		["#DFD4CF", "#521C14", "#B08470"], // 咖啡与奶油 (Coffee & Cream)
		["#7A9DB1", "#9FBCAD", "#9681AC"], // 雾中花园 (Misty Garden)
		["#6B7A89", "#CDBCA8", "#675C5A"], // 阴天海岸 (Overcast Coast)
		["#D5BFB9", "#FBFAF5", "#945A23"], // 焦糖奶泡 (Caramel Foam)
		["#EECCD3", "#80C4B7", "#EEC95C"], // 樱花与柠檬 (Sakura & Lemon)
		["#3A6D80", "#F3CD53", "#D56729"], // 油画海港 (Oil Painting Harbor)
		["#4D181C", "#144058", "#E58D2E"], // 深夜爵士 (Late Night Jazz)
		["#144058", "#E58D2E", "#DD671E"], // 秋日暮光 (Autumn Twilight)
		["#4CAABC", "#72C2C9", "#9FA65A"], // 湖畔青苔 (Lakeside Moss)
		["#CEE6F2", "#E9B796", "#E3867D"], // 晨雾玫瑰 (Morning Mist Rose)
		["#DE9DC2", "#9EE8E1", "#573C33"], // 薄荷巧克力 (Mint Chocolate)
		["#735DA5", "#D3C5E5", "#8EC9BC"], // 鸢尾与薄荷 (Iris & Mint)
		["#2963A2", "#4CAABC", "#72C2C9"], // 蓝色珊瑚礁 (Blue Lagoon)
		["#355952", "#EAB63E", "#FAF6E7"], // 墨绿与蜜糖 (Dark Green & Honey)
		["#FF6B6B", "#4ECDC4", "#45B7D1"], // 珊瑚蓝绿
		["#96CEB4", "#FFEAA7", "#DDA0DD"], // 薄荷黄紫
		["#FF8A80", "#FFD54F", "#81C784"], // 粉黄绿
		["#BA68C8", "#64B5F6", "#4DB6AC"], // 紫蓝青
		["#FFB74D", "#F06292", "#7986CB"], // 橙粉蓝
		["#A5D6A7", "#FFCC02", "#FF7043"], // 绿黄橙
		["#CE93D8", "#80CBC4", "#FFAB91"], // 紫青橙
		["#FFCDD2", "#C8E6C9", "#BBDEFB"], // 粉绿蓝
		["#E1BEE7", "#B39DDB", "#90CAF9"], // 淡紫蓝
		["#FFECB3", "#DCEDC8", "#B2DFDB"], // 黄绿青
		["#F8BBD9", "#B2EBF2", "#FFCDD2"], // 粉青红
		["#D1C4E9", "#C5E1A5", "#FFE0B2"], // 紫绿橙
	];

	// 处理颜色选择
	const handleColorSelect = (color, index) => {
		const newColors = [...colors];
		newColors[index] = color;
		console.log(
			"Color selected:",
			color,
			"for index:",
			index,
			"new colors:",
			newColors
		);
		onColorsChange(newColors);
	};

	// 处理颜色圆点点击
	const handleColorDotClick = (index) => {
		colorInputRefs.current[index]?.click();
	};

	// 处理预设颜色选择
	const handlePresetSelect = (presetColors) => {
		onColorsChange(presetColors);
		setShowPresets(false);
	};

	// 生成渐变背景样式
	const getGradientStyle = () => {
		return {
			background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]}, ${colors[2]})`,
		};
	};

	return (
		<div className="relative">
			{/* 颜色选择器显示区域 */}
			<div
				className="px-6 py-4 rounded-2xl flex justify-between items-center transition-all duration-300"
				style={getGradientStyle()}>
				<div className="flex flex-col text-white">
					<h1>Colors</h1>
					<span className="text-xs font-light text-gray-200">
						Pick your favourite colors
					</span>
				</div>

				<div className="flex gap-4 items-center justify-end">
					{/* 3个颜色圆点 */}
					<div className="flex gap-2">
						{colors.map((color, index) => (
							<div
								key={index}
								className="relative">
								<div
									className="w-8 h-8 rounded-full border-2 border-white cursor-pointer transition-all duration-200 hover:scale-110"
									style={{ backgroundColor: color }}
									onClick={(e) => {
										e.stopPropagation();
										handleColorDotClick(index);
									}}
								/>

								{/* 隐藏的颜色选择器 */}
								<input
									ref={(el) => (colorInputRefs.current[index] = el)}
									type="color"
									value={color}
									onChange={(e) => handleColorSelect(e.target.value, index)}
									className="absolute opacity-0 w-0 h-0 pointer-events-none"
								/>
							</div>
						))}
					</div>

					<SpringBtn
						onClick={() => setShowPresets(true)}
						className={`h-12 w-12 text-center rounded-full transition text-gray-800 bg-white hover:bg-gray-100`}>
						<Palette className="inline-block w-5 h-5" />
					</SpringBtn>
				</div>
			</div>

			{/* 预设颜色弹窗 */}
			<PopUp
				visibility={showPresets}
				onPopup={setShowPresets}
				touchClose={true}>
				<div className="w-full max-w-sm">
					<div className="text-center mb-4">
						<h3 className="text-lg font-semibold text-gray-800">
							Palette Presets
						</h3>
						<p className="text-sm text-gray-500 font-light">
							Pick your favorite palette
						</p>
					</div>

					{/* 预设颜色列表 */}
					<div className="max-h-80 overflow-y-auto space-y-3">
						{colorPresets.map((preset, index) => (
							<SpringBtn
								key={index}
								onClick={() => handlePresetSelect(preset)}
								className="w-full rounded-xl hover:opacity-80 transition-all duration-200">
								<div className="flex h-16 rounded-lg overflow-hidden">
									{preset.map((color, colorIndex) => (
										<div
											key={colorIndex}
											className="flex-1 h-full w-8"
											style={{ backgroundColor: color }}
										/>
									))}
								</div>
							</SpringBtn>
						))}
					</div>
				</div>
			</PopUp>
		</div>
	);
}

export default ColorPicker;
